/**
 * @file CustomerGreeting.tsx
 * @description Personalized "Hi {name}" header for the customer dashboard.
 *   Fetches the user's profile via /me; falls back to the email local-part
 *   if no full_name has been set yet (a customer can come in via the
 *   invite flow without filling in their name).
 * @module Components
 * @security GET /me is the authenticated endpoint that returns the user's
 *   own decrypted profile. The greeting is best-effort; if the call fails
 *   we render a generic greeting rather than blocking the page.
 */

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMe, type MeProfile } from '../../lib/me-api';

export function CustomerGreeting(): JSX.Element {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<MeProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch(() => {
        /* best-effort: silently fall through to the generic greeting */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName = profile?.full_name?.trim() ?? deriveFallback(profile?.email);
  return (
    <div className="space-y-1">
      <h1 className="text-2xl font-bold text-slate-900">
        {displayName !== null
          ? t('dashboard.greet_named', { name: displayName })
          : t('dashboard.greet_generic')}
      </h1>
      <p className="text-slate-500 text-sm">{t('dashboard.projects_subtitle')}</p>
    </div>
  );
}

// Pull the part of the email before "@" so the empty-name case still
// looks personal. e.g. "dana@example.com" → "dana".
function deriveFallback(email: string | undefined): string | null {
  if (email === undefined) return null;
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}
